import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getGroupAnalysis, getGroupAnalysisEvidence, getGroupDashboard, getGroupMessageContext, listGroupAnalyses, listGroupAnalysesToday, reviewGroupComplianceFlag, runGroupAnalysis } from '../../api/adminApi.js';
import type {
  GroupAnalysisEvidence,
  GroupAnalysisLabels,
  GroupDashboardChurnItem,
  GroupDashboardMissedItem,
  GroupDashboardRiskItem,
  GroupAnalysisOverviewRow,
  GroupAnalysisResponse,
  GroupAnalysisSort,
  GroupAnalysisTier,
  GroupAnalysisTodayReason,
  GroupAnalysisView,
  GroupAnalysisViewCounts,
  GroupComplianceReviewStatus,
  GroupDashboardEvidenceRule,
  GroupDashboardTotals,
  ListConversationsPagination,
} from '../../api/types.js';
import { AnalysisDetail } from './AnalysisDetail.js';
import { shouldStartOnEntry } from './analysis-state.js';
import { readGroupId, writeGroupId } from '../groups/group-navigation.js';
import { MetricList } from '../dashboard/MetricList.js';
import { createDetailSelection, type DetailSelection } from './detail-selection.js';
import { EvidenceSheet, type EvidenceSheetData } from './EvidenceSheet.js';
import {
  ANALYSIS_VIEW_GROUPS,
  analysisViewTab,
  complianceLabel,
  completenessLabel,
  formatDateTime,
  intentDeltaLabel,
  intentLabel,
  isAnalysisView,
  lastSpeakerLabel,
  lifecycleStageLabel,
  moneyRangeLabel,
  pipelineValueLabel,
  profileLabel,
  runErrorLabel,
  stageLabel,
  TIER_DESC,
  TIER_LABELS,
  TIER_NAMES,
  tierLabel,
  todayReasonLabel,
} from './labels.js';
import '../../styles/analysis.css';

const PAGE_SIZE = 50;
const RUN_POLL_MS = 5_000;
const RUN_POLL_MAX_MS = 15 * 60_000;
/** 页签计数与名单视图的周期；分析页固定近 30 天（看板有周期切换）。 */
const VIEW_DAYS = 30;

/** 从 URL 读初始页签（看板卡片跳转带 ?view=）；没带时老板落全部，业务员落今日名单。 */
function readViewFromUrl(fallback: GroupAnalysisView): GroupAnalysisView {
  const value = new URLSearchParams(window.location.search).get('view');
  return isAnalysisView(value) ? value : fallback;
}

function writeViewToUrl(view: GroupAnalysisView): void {
  const url = new URL(window.location.href);
  if (view === 'all') url.searchParams.delete('view');
  else url.searchParams.set('view', view);
  window.history.replaceState(window.history.state, '', url);
}

/**
 * 智能分析独立页面：页首四张可点卡 + 三组页签（带计数）+ 左侧客户群列表 + 右侧分档排行榜/详情。
 * 只列 registry 映射工作号下的适用客户群，权限沿用群所属工作号范围；名单视图与计数由后端
 * ledger 同口径给出（本页不做任何口径计算）；stage/risk/fact 标签来自 API 返回的 Profile labels。
 */
export function AnalysisConsole({ isAdmin, staffName, onToast, onOpenGroup, consumeRunIntent }: {
  isAdmin: boolean;
  staffName: string;
  onToast(message: string, type?: 'success' | 'error'): void;
  onOpenGroup(row: Pick<GroupAnalysisOverviewRow, 'conversationId'>): void;
  consumeRunIntent(id: number): boolean;
}) {
  const [rows, setRows] = useState<TodayRow[]>([]);
  const [labels, setLabels] = useState<Record<string, GroupAnalysisLabels>>({});
  const [counts, setCounts] = useState<GroupAnalysisViewCounts | null>(null);
  const [totals, setTotals] = useState<GroupDashboardTotals | null>(null);
  const [pagination, setPagination] = useState<ListConversationsPagination & { total: number }>({ ...emptyPagination(), total: 0 });
  const [view, setView] = useState<GroupAnalysisView>(() => readViewFromUrl(isAdmin ? 'all' : 'today'));
  const [query, setQuery] = useState('');
  const [stage, setStage] = useState('');
  const [intent, setIntent] = useState('');
  const [tier, setTier] = useState(() => { const value = new URLSearchParams(window.location.search).get('tier'); return value && ['A', 'B', 'C', 'D'].includes(value) ? value : ''; });
  const [sort, setSort] = useState<GroupAnalysisSort>('tier_desc');
  const [includeUnanalyzed, setIncludeUnanalyzed] = useState(false);
  const [lifecycle, setLifecycle] = useState('');
  const [dealSignalsOnly, setDealSignalsOnly] = useState(false);
  const [dormantOnly, setDormantOnly] = useState(false);
  const [reasonCounts, setReasonCounts] = useState<Record<GroupAnalysisTodayReason, number> | null>(null);
  /** 漏跟/流失/风险名单的重载键：核实写入后 +1。 */
  const [listReload, setListReload] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [analysis, setAnalysis] = useState<GroupAnalysisResponse | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [evidence, setEvidence] = useState<EvidenceSheetData | null>(null);
  const pollTimer = useRef<number | null>(null);
  const pollStartedAt = useRef(0);
  const polling = useRef(false);

  const stopPolling = useCallback(() => {
    polling.current = false;
    if (pollTimer.current !== null) {
      window.clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);

  // 业务员的“今天该跟的”走今日名单接口（同一集合，另带“为什么今天做”），其余走通用名单。
  const salesToday = !isAdmin && view === 'today';
  const loadList = useCallback(async (nextOffset: number) => {
    setLoading(true);
    try {
      if (salesToday) {
        const response = await listGroupAnalysesToday({
          q: query.trim() || undefined,
          days: VIEW_DAYS,
          limit: PAGE_SIZE,
          offset: nextOffset,
        });
        setRows(response.rows);
        setLabels(response.labels);
        setPagination(response.pagination);
        setCounts(response.counts);
        setReasonCounts(response.reasonCounts);
        return;
      }
      const response = await listGroupAnalyses({
        q: query.trim() || undefined,
        stage: stage || undefined,
        intent: intent || undefined,
        tier: tier || undefined,
        lifecycle: (lifecycle || undefined) as 'repeat' | 'customer' | 'intent' | 'new' | undefined,
        dealSignals: dealSignalsOnly,
        dormant: dormantOnly,
        sort,
        includeUnanalyzed,
        view,
        days: VIEW_DAYS,
        limit: PAGE_SIZE,
        offset: nextOffset,
      });
      setRows(response.rows);
      setLabels(response.labels);
      setPagination(response.pagination);
      if (response.counts) setCounts(response.counts);
    } catch (error) {
      onToast(error instanceof Error ? error.message : '智能分析列表加载失败', 'error');
    } finally {
      setLoading(false);
    }
  }, [dealSignalsOnly, dormantOnly, includeUnanalyzed, intent, lifecycle, onToast, query, salesToday, sort, stage, tier, view]);

  // 四张卡里的金额与风险单条数来自看板接口（sales 角色金额已脱敏为 null）；失败只影响卡片副行，不打断名单。
  useEffect(() => {
    let cancelled = false;
    getGroupDashboard(VIEW_DAYS).then(
      (response) => { if (!cancelled) setTotals(response); },
      () => { if (!cancelled) setTotals(null); },
    );
    return () => { cancelled = true; };
  }, []);

  // 轮询/详情完成后的副作用需要最新的回调与状态，经 ref 转发给只创建一次的 selection。
  const handlersRef = useRef({
    onLoaded: (_id: number, _analysis: GroupAnalysisResponse) => {},
    onFailed: (_message: string) => {},
  });
  handlersRef.current = {
    onLoaded: (id, response) => {
      setAnalysis(response);
      setDetailError(null);
      if (consumeRunIntent(id) && shouldStartOnEntry(response)) {
        void runFor(id);
        return;
      }
      if (response.latestRun?.status === 'running') {
        if (!polling.current) {
          polling.current = true;
          pollStartedAt.current = Date.now();
        }
        setRunning(true);
        if (Date.now() - pollStartedAt.current < RUN_POLL_MAX_MS) {
          stopPollingTimerOnly();
          pollTimer.current = window.setTimeout(() => selectionRef.current?.refresh(id), RUN_POLL_MS);
          return;
        }
      }
      const wasPolling = polling.current;
      polling.current = false;
      setRunning(false);
      if (wasPolling) {
        if (response.latestRun?.status === 'succeeded') onToast('分析完成', 'success');
        else if (response.latestRun?.status === 'failed') onToast(`分析失败：${runErrorLabel(response.latestRun.errorCode)}`, 'error');
        void loadList(offset);
      }
    },
    onFailed: (message) => {
      polling.current = false;
      setRunning(false);
      setAnalysis(null);
      setDetailError(message);
      onToast(message, 'error');
    },
  };
  const stopPollingTimerOnly = () => {
    if (pollTimer.current !== null) {
      window.clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  };

  const selectionRef = useRef<DetailSelection | null>(null);
  if (!selectionRef.current) {
    selectionRef.current = createDetailSelection({
      load: getGroupAnalysis,
      emit: (event) => {
        switch (event.type) {
          case 'selected':
            setActiveId(event.id);
            setDetailError(null);
            setAnalysis(null);
            setEvidence(null);
            break;
          case 'cleared':
            setActiveId(null);
            setDetailError(null);
            setAnalysis(null);
            setEvidence(null);
            setRunning(false);
            break;
          case 'loaded':
            handlersRef.current.onLoaded(event.id, event.analysis);
            break;
          case 'load_failed':
            consumeRunIntent(event.id);
            handlersRef.current.onFailed(event.message);
            break;
        }
      },
    });
  }
  const selection = selectionRef.current;

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setOffset(0);
      void loadList(0);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [loadList]);

  // 切换筛选/搜索/页签时清空选中：进行中的详情请求返回后不得再写入已清空的选中状态。
  const filtersKey = JSON.stringify([query, stage, intent, tier, lifecycle, dealSignalsOnly, dormantOnly, sort, includeUnanalyzed, view]);
  const prevFiltersKey = useRef(filtersKey);
  useEffect(() => {
    if (prevFiltersKey.current === filtersKey) return;
    prevFiltersKey.current = filtersKey;
    stopPolling();
    selection.clear();
    writeGroupId(null);
  }, [filtersKey, selection, stopPolling]);

  useEffect(() => {
    const id = readGroupId(window.location.search);
    if (id !== null) selection.select(id);
    return () => {
      stopPolling();
      selection.clear();
    };
  }, [selection, stopPolling]);

  const switchView = useCallback((next: GroupAnalysisView) => {
    setView(next);
    writeViewToUrl(next);
  }, []);

  const selectRow = useCallback((id: number) => {
    stopPolling();
    setRunning(false);
    writeGroupId(id);
    selection.select(id);
  }, [selection, stopPolling]);

  const clearSelection = useCallback(() => {
    stopPolling();
    selection.clear();
    writeGroupId(null);
  }, [selection, stopPolling]);

  const runFor = useCallback(async (id: number) => {
    setRunning(true);
    try {
      const response = await runGroupAnalysis(id);
      if (!selection.isSelected(id)) return;
      if (response.status === 'running') {
        onToast('已开始分析，通常需要 1～4 分钟', 'success');
        polling.current = true;
        pollStartedAt.current = Date.now();
        stopPollingTimerOnly();
        selection.refresh(id);
      } else {
        setRunning(false);
        onToast(`未开始分析：${response.status}`, 'error');
      }
    } catch (error) {
      if (!selection.isSelected(id)) return;
      setRunning(false);
      selection.refresh(id);
      onToast(error instanceof Error ? error.message : '触发分析失败', 'error');
    }
  }, [onToast, selection]);

  /** 快照证据：判定句 = 模型引用的原话，节选走 messages/:id/context。 */
  const openEvidence = useCallback(async (item: GroupAnalysisEvidence) => {
    if (!activeId) return;
    const title = `证据 · 消息 #${item.messageId}`;
    setEvidence({ title, anchorMessageId: item.messageId, quote: item.quote, verdict: null, messages: [], loading: true });
    try {
      const response = await getGroupMessageContext(activeId, item.messageId, 3);
      setEvidence((current) => current?.anchorMessageId === item.messageId ? { ...current, messages: response.messages, loading: false } : current);
    } catch (error) {
      const message = error instanceof Error ? error.message : '证据上下文加载失败';
      setEvidence((current) => current?.anchorMessageId === item.messageId ? { ...current, loading: false, error: message } : current);
    }
  }, [activeId]);

  /** 规则证据（跟进过程卡“看原话”）：判定说明由后端按规则现算。 */
  const openRuleEvidence = useCallback(async (conversationId: number, messageId: number, rule: GroupDashboardEvidenceRule) => {
    const title = `原话 · 消息 #${messageId}`;
    setEvidence({ title, anchorMessageId: messageId, quote: null, verdict: null, messages: [], loading: true });
    try {
      const response = await getGroupAnalysisEvidence(conversationId, messageId, rule);
      setEvidence((current) => current?.anchorMessageId === messageId
        ? { ...current, verdict: response.verdict.text, messages: response.messages, loading: false }
        : current);
    } catch (error) {
      const message = error instanceof Error ? error.message : '原话加载失败';
      setEvidence((current) => current?.anchorMessageId === messageId ? { ...current, loading: false, error: message } : current);
    }
  }, []);

  /** 风险单核实：写后刷新详情（状态）、名单与风险单列表（计数缓存已在后端失效）。 */
  const reviewComplianceFor = useCallback(async (conversationId: number, flagKey: string, status: GroupComplianceReviewStatus) => {
    try {
      await reviewGroupComplianceFlag(conversationId, flagKey, status);
      onToast(status === 'confirmed' ? '已确认属实' : status === 'false_positive' ? '已标记误报' : '已撤回核实', 'success');
      if (activeId === conversationId) selectionRef.current?.refresh(conversationId);
      setListReload((current) => current + 1);
      void loadList(offset);
    } catch (error) {
      onToast(error instanceof Error ? error.message : '核实失败', 'error');
    }
  }, [activeId, loadList, offset, onToast]);
  const reviewCompliance = useCallback(async (flagKey: string, status: GroupComplianceReviewStatus) => {
    if (activeId) await reviewComplianceFor(activeId, flagKey, status);
  }, [activeId, reviewComplianceFor]);

  const activeRow = analysis ? {
    ...analysis.group,
    profile: analysis.profile ?? '',
    latestMessageId: analysis.latestMessageId,
    followUp: analysis.followUp,
  } : rows.find((row) => row.conversationId === activeId) ?? null;
  const stageOptions = useMemo(() => {
    const options = new Map<string, string>();
    for (const profileLabels of Object.values(labels)) {
      for (const [value, label] of Object.entries(profileLabels.stages)) {
        if (!options.has(value)) options.set(value, label);
      }
    }
    return [...options.entries()];
  }, [labels]);
  const rowLabels = useCallback((row: GroupAnalysisOverviewRow) => labels[row.profile] ?? null, [labels]);
  const activeTab = analysisViewTab(view);

  return (
    <main className={`analysis-console sr${activeId !== null ? ' has-detail' : ''}`}>
      <header className="analysis-console-header">
        <div>
          <h1>{isAdmin ? '智能分析' : '我的客户群'}</h1>
          <p>
            {isAdmin
              ? `客户群需求与推进状态，结果供人工判断，不自动发送 · 计数按近 ${VIEW_DAYS} 天`
              : `系统从你名下 ${counts?.all ?? '—'} 个群里排好今天要做的事 · 做完不用汇报（AI 从群聊自动记账）`}
          </p>
        </div>
        <button type="button" onClick={() => void loadList(offset)} disabled={loading}>刷新</button>
      </header>

      {isAdmin ? (
      <section className="analysis-cards" aria-label="四张卡">
        <button type="button" className={`analysis-card-btn${view === 'pipeline' ? ' active' : ''}`} onClick={() => switchView('pipeline')}>
          <span>谁值钱</span>
          <strong>{counts ? `在途 ${counts.pipeline} 群` : '—'}</strong>
          <small>{totals ? `在途 ${moneyRangeLabel(totals.pipeline.min, totals.pipeline.max)}` : ' '}</small>
        </button>
        <button type="button" className={`analysis-card-btn${view === 'today' ? ' active' : ''}`} onClick={() => switchView('today')}>
          <span>谁要跟</span>
          <strong>{counts ? `今天该跟 ${counts.today}` : '—'}</strong>
          <small>{counts ? `成交待核 ${counts.dealPending} · 新群待破冰 ${counts.new}` : ' '}</small>
        </button>
        <button type="button" className={`analysis-card-btn warn${view === 'missed' ? ' active' : ''}`} onClick={() => switchView('missed')}>
          <span>谁在丢</span>
          <strong>{counts ? `漏跟 ${counts.missed}` : '—'}</strong>
          <small>{counts ? `沉睡 ${counts.dormant} · 流失 ${counts.churn}` : ' '}</small>
        </button>
        <button type="button" className={`analysis-card-btn warn${view === 'risk' ? ' active' : ''}`} onClick={() => switchView('risk')}>
          <span>谁出事</span>
          <strong>{counts ? `风险单 ${counts.risk} 群` : '—'}</strong>
          <small>{totals ? `待核实 ${totals.risk.pending} 条 · 已确认 ${totals.risk.confirmed}` : ' '}</small>
        </button>
      </section>
      ) : null}

      <nav className="analysis-tabs" aria-label="名单页签">
        {ANALYSIS_VIEW_GROUPS.map((group) => (
          <div key={group.title} className="analysis-tab-group">
            <span className="analysis-tab-group-title">{group.title}</span>
            {group.tabs.map((tab) => (
              <button
                key={tab.view}
                type="button"
                className={tab.view === view ? 'active' : ''}
                onClick={() => switchView(tab.view)}
              >
                {tab.label}
                <em>{counts ? counts[tab.countKey] : '·'}</em>
              </button>
            ))}
          </div>
        ))}
        <p className="analysis-tab-hint">
          <span>老板：{activeTab.boss}</span>
          <span>销售：{activeTab.sales}</span>
        </p>
      </nav>

      <div className="analysis-workspace">
        <aside className="analysis-list-pane">
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="搜索群名"
            aria-label="搜索客户群"
          />
          <div className="analysis-filters">
            <select value={stage} onChange={(event) => setStage(event.target.value)} aria-label="阶段筛选">
              <option value="">全部阶段</option>
              {stageOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <select value={intent} onChange={(event) => setIntent(event.target.value)} aria-label="意向筛选">
              <option value="">全部意向</option>
              <option value="high">高意向</option>
              <option value="medium">中意向</option>
              <option value="low">低意向</option>
              <option value="unknown">未知</option>
            </select>
            <select value={tier} onChange={(event) => setTier(event.target.value)} aria-label="档位筛选">
              <option value="">全部档位</option>
              {Object.entries(TIER_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <select value={lifecycle} onChange={(event) => setLifecycle(event.target.value)} aria-label="客群筛选">
              <option value="">全部客群</option>
              <option value="repeat">复购老客</option>
              <option value="customer">成交客</option>
              <option value="intent">意向未买</option>
              <option value="new">新客</option>
            </select>
            <select value={sort} onChange={(event) => setSort(event.target.value as GroupAnalysisSort)} aria-label="排序">
              <option value="tier_desc">档位优先</option>
              <option value="intent_desc">意向高→低</option>
              <option value="intent_asc">意向低→高</option>
              <option value="analyzed_desc">最近分析</option>
              <option value="inbound_desc">消息最多</option>
            </select>
            <label title={view === 'all' ? '' : '名单视图自动包含未分析群'}>
              <input type="checkbox" checked={includeUnanalyzed || view !== 'all'} disabled={view !== 'all'} onChange={(event) => setIncludeUnanalyzed(event.target.checked)} />
              含未分析
            </label>
            <label title="当前快照有成交信号（转账/付款/发货/单号/约到店）">
              <input type="checkbox" checked={dealSignalsOnly} onChange={(event) => setDealSignalsOnly(event.target.checked)} />
              有成交信号
            </label>
            <label title="最后一条客户消息距今 ≥ 30 天">
              <input type="checkbox" checked={dormantOnly} onChange={(event) => setDormantOnly(event.target.checked)} />
              沉睡 30 天+
            </label>
          </div>
          <div className="analysis-list">
            {rows.map((row) => (
              <button
                key={row.conversationId}
                type="button"
                className={`analysis-list-item${row.conversationId === activeId ? ' active' : ''}`}
                onClick={() => selectRow(row.conversationId)}
              >
                <strong>{row.clusterName || `群 ${row.clusterId}`}</strong>
                <span>{row.snapshot ? row.snapshot.headline : '尚未分析'}</span>
                <small>
                  {row.snapshot ? `${tierLabel(row.snapshot.tier)} · ${stageLabel(rowLabels(row), row.snapshot.stage)} · 意向${intentLabel(row.snapshot.intentLevel)}` : `入站 ${row.inboundCount} 条`}
                  <StatusMark row={row} />
                </small>
              </button>
            ))}
            {!loading && rows.length === 0 ? <div className="analysis-empty">暂无符合条件的客户群</div> : null}
          </div>
          <footer className="analysis-pagination">
            <button className="button ghost" type="button" disabled={!pagination.hasPrevious || loading} onClick={() => { const next = pagination.previousOffset ?? 0; setOffset(next); void loadList(next); }}>上一页</button>
            <span>{loading ? '加载中…' : `${pagination.count === 0 ? 0 : pagination.offset + 1}-${pagination.offset + pagination.count} / ${pagination.total}`}</span>
            <button className="button ghost" type="button" disabled={!pagination.hasNext || loading} onClick={() => { if (pagination.nextOffset === null) return; setOffset(pagination.nextOffset); void loadList(pagination.nextOffset); }}>下一页</button>
          </footer>
        </aside>
        <section className="analysis-main-pane">
          {activeRow && !detailError ? (
            <AnalysisDetail
              key={activeId}
              row={activeRow}
              analysis={analysis}
              running={running}
              isAdmin={isAdmin}
              evidence={evidence}
              onBack={clearSelection}
              onRun={() => { if (activeId !== null) void runFor(activeId); }}
              onOpenGroup={() => onOpenGroup(activeRow)}
              onOpenEvidence={(item) => void openEvidence(item)}
              onOpenRuleEvidence={(messageId, rule) => void openRuleEvidence(activeRow.conversationId, messageId, rule)}
              onCloseEvidence={() => setEvidence(null)}
              onReviewCompliance={isAdmin ? (flagKey, status) => void reviewCompliance(flagKey, status) : undefined}
            />
          ) : activeId !== null ? (
            <div className="analysis-empty" role="status">
              <p>{detailError ?? '正在加载该群分析…'}</p>
              <button type="button" className="button ghost" onClick={clearSelection}>返回名单</button>
              {detailError ? <button type="button" className="button" onClick={() => selection.refresh(activeId)}>重试加载</button> : null}
            </div>
          ) : salesToday ? (
            <TodayCards rows={rows} offset={pagination.offset} total={pagination.total} reasonCounts={reasonCounts} loading={loading} staffName={staffName} onSelect={selectRow} onOpenGroup={onOpenGroup} onOpenView={switchView} />
          ) : view === 'missed' || view === 'churn' || view === 'risk' ? (
            <EvidenceListPane
              view={view}
              isAdmin={isAdmin}
              reloadKey={listReload}
              onToast={onToast}
              onSelect={selectRow}
              onOpenEvidence={(conversationId, messageId, rule) => void openRuleEvidence(conversationId, messageId, rule)}
              onReview={(conversationId, flagKey, status) => void reviewComplianceFor(conversationId, flagKey, status)}
            />
          ) : (
            <RankingTable rows={rows} view={view} total={pagination.total} onSelect={selectRow} />
          )}
          {!activeRow && evidence ? <EvidenceSheet data={evidence} onClose={() => setEvidence(null)} /> : null}
        </section>
      </div>
    </main>
  );
}

const TIER_ORDER: ReadonlyArray<GroupAnalysisTier | 'none'> = ['A', 'B', 'C', 'D', 'none'];

type TodayRow = GroupAnalysisOverviewRow & { reasons?: GroupAnalysisTodayReason[] };

/** 业务员今日名单：每卡 序号 · 群名 · 档位 · 客群 · 最后互动 · 为什么今天做 · 去跟进 / 看分析。 */
function TodayCards({ rows, offset, total, reasonCounts, loading, staffName, onSelect, onOpenGroup, onOpenView }: {
  rows: TodayRow[];
  offset: number;
  total: number;
  reasonCounts: Record<GroupAnalysisTodayReason, number> | null;
  loading: boolean;
  staffName: string;
  onSelect(id: number): void;
  onOpenGroup(row: GroupAnalysisOverviewRow): void;
  onOpenView(view: GroupAnalysisView): void;
}) {
  return (
    <div className="analysis-ranking">
      <header>
        <h2>📋 今日名单 · {staffName} <span className="analysis-ranking-total">{loading && rows.length === 0 ? '…' : total}</span></h2>
        <p><span className="badge b-line">本期新增 · 演示数据</span></p>
        <p>系统从你名下 {total || 6} 位客户里排好的今天要做的事 · 做完不用汇报（AI 从对话自动记账）· 🔒 身价金额按老板的权限设置隐藏</p>
      </header>
      <div className="analysis-mini-cards">
        <div><span>今天要做</span><strong>{loading && rows.length === 0 ? '—' : total}</strong><small>件 · 前几件最急</small></div>
        <div><span>承诺到期</span><strong>{reasonCounts?.next_step_overdue ?? '—'}</strong><small>个 · 到期前回一句就行</small></div>
        <button type="button" onClick={() => onOpenView('missed')}><span>我的漏跟</span><strong>{reasonCounts?.missed ?? '—'}</strong><small>条 · 客户在等你 · 点开看原话</small></button>
      </div>
      <div className="analysis-today-cards">
        {rows.map((row, index) => (
          <article key={row.conversationId} className="analysis-today-card">
            <div className="analysis-today-card-head">
              <span className="analysis-today-index">{offset + index + 1}</span>
              <strong>{row.clusterName || `群 ${row.clusterId}`}</strong>
              {row.snapshot ? <span className={`analysis-tier tier-${row.snapshot.tier ?? 'none'}`}>{tierLabel(row.snapshot.tier)}</span> : <span className="analysis-tier tier-none">未分析</span>}
              <span className="analysis-today-lifecycle">{lifecycleStageLabel(row.snapshot?.lifecycleStage)}</span>
            </div>
            <div className="analysis-today-reasons">
              {(row.reasons ?? []).map((reason) => <em key={reason} className={`analysis-flag reason-${reason}`}>{todayReasonLabel(reason)}</em>)}
            </div>
            <p className="analysis-today-why">{row.snapshot?.headline ?? '尚未分析：先看群里最新一句'}</p>
            <div className="analysis-today-meta">
              <span className={row.followUp?.lastSpeaker === 'customer' ? 'waiting' : ''}>{lastSpeakerLabel(row.followUp?.lastSpeaker)} · {formatDateTime(row.followUp?.lastMessageAt ?? row.lastMessageAt)}</span>
              {row.snapshot?.nextStep?.dueDate ? <span>{row.snapshot.nextStep.owner === 'service' ? '我方' : '客户'}承诺 {row.snapshot.nextStep.dueDate}{row.followUp?.nextStepOverdue ? '（已到期）' : ''}</span> : null}
            </div>
            <div className="analysis-today-actions">
              <button type="button" className="button" onClick={() => onOpenGroup(row)}>去跟进</button>
              <button type="button" className="button ghost" onClick={() => onSelect(row.conversationId)}>看分析</button>
            </div>
          </article>
        ))}
        {rows.length === 0 ? <div className="analysis-empty">{loading ? '正在从群聊里整理今天要做的事…' : '今天没有需要跟的群'}</div> : null}
      </div>
    </div>
  );
}

/** 排行榜：按档位 A–D（+未评级）分组折叠；列含跟进过程（谁在等 / 超时 / 漏跟）。 */
function RankingTable({ rows, view, total, onSelect }: {
  rows: GroupAnalysisOverviewRow[];
  view: GroupAnalysisView;
  total: number;
  onSelect(id: number): void;
}) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const groups = useMemo(() => {
    const byTier = new Map<GroupAnalysisTier | 'none', GroupAnalysisOverviewRow[]>();
    for (const row of rows) {
      const key = row.snapshot?.tier ?? 'none';
      byTier.set(key, [...(byTier.get(key) ?? []), row]);
    }
    return TIER_ORDER.flatMap((key) => {
      const items = byTier.get(key);
      return items && items.length > 0 ? [{ key, items }] : [];
    });
  }, [rows]);
  const tab = analysisViewTab(view);

  return (
    <div className="analysis-ranking">
      <header>
        <h2>{tab.label}<span className="analysis-ranking-total">{total}</span></h2>
        <p>按档位 A→D 分组，同档看在途金额与意向分 · 点击行进入详情 · “客户在等”= 最后一句是客户说的</p>
      </header>
      <div className="analysis-table-wrap">
        <table>
          <thead>
            <tr>
              <th>档位</th>
              <th>客户群</th>
              <th>客群</th>
              <th>品类</th>
              <th>在途金额</th>
              <th>预算 / 总价</th>
              <th>意向 · 动向</th>
              <th>成交信号</th>
              <th>下一步承诺</th>
              <th>需求完整度</th>
              <th>最后互动</th>
              <th>状态</th>
              <th>AI 一句话</th>
            </tr>
          </thead>
          {groups.map((group) => (
            <tbody key={group.key}>
              <tr className="analysis-tier-row" onClick={() => setCollapsed((current) => ({ ...current, [group.key]: !current[group.key] }))}>
                <td colSpan={13}>
                  <span className={`analysis-tier tier-${group.key}`}>{group.key === 'none' ? '未评级' : `${group.key} · ${TIER_NAMES[group.key]}`}</span>
                  <span className="analysis-tier-count">{group.items.length} 群 · 沉睡 {group.items.filter((row) => row.followUp?.dormant).length} · 有承诺 {group.items.filter((row) => row.snapshot?.nextStep).length}</span>
                  <span className="analysis-tier-desc">{TIER_DESC[group.key]}</span>
                  <span className="analysis-tier-toggle">{collapsed[group.key] ? '展开' : '收起'}</span>
                </td>
              </tr>
              {collapsed[group.key] ? null : group.items.map((row) => (
                <tr key={row.conversationId} onClick={() => onSelect(row.conversationId)}>
                  <td>{row.snapshot ? <span className={`analysis-tier tier-${row.snapshot.tier ?? 'none'}`}>{tierLabel(row.snapshot.tier)}</span> : '-'}</td>
                  <td className="analysis-cell-name"><strong>{row.clusterName || `群 ${row.clusterId}`}</strong><small>{profileLabel(row.profile)} · 入站 {row.inboundCount} 条</small></td>
                  <td>{lifecycleStageLabel(row.snapshot?.lifecycleStage)}</td>
                  <td className="analysis-cell-fact">{row.snapshot?.keyFacts.category ?? '-'}</td>
                  <td>{pipelineValueLabel(row.snapshot?.pipelineValue)}</td>
                  <td className="analysis-cell-fact">{row.snapshot?.keyFacts.amount ?? '-'}</td>
                  <td>
                    {row.snapshot ? <span className={`analysis-intent intent-${row.snapshot.intentLevel}`}>{intentLabel(row.snapshot.intentLevel)} {row.snapshot.intentScore ?? `${Math.round(row.snapshot.intentConfidence * 100)}%`}</span> : '-'}
                    {row.snapshot?.intentDelta !== null && row.snapshot?.intentDelta !== undefined ? <em className={`analysis-delta ${row.snapshot.intentDelta > 0 ? 'up' : row.snapshot.intentDelta < 0 ? 'down' : ''}`} title="与上一份快照相比">{intentDeltaLabel(row.snapshot.intentDelta)}</em> : null}
                  </td>
                  <td>{row.snapshot ? (row.snapshot.dealSignalCount > 0 ? <span className="analysis-signal">待核 ×{row.snapshot.dealSignalCount}</span> : '无') : '-'}</td>
                  <td className="analysis-cell-next">
                    {row.snapshot?.nextStep ? (
                      <>
                        <span className={`analysis-owner owner-${row.snapshot.nextStep.owner}`}>{row.snapshot.nextStep.owner === 'service' ? '我方' : '客户'}</span>
                        {row.snapshot.nextStep.dueDate ? <time className={row.followUp?.nextStepOverdue ? 'overdue' : ''}>{row.snapshot.nextStep.dueDate}{row.followUp?.nextStepOverdue ? ' 已到期' : ''}</time> : null}
                        <small title={row.snapshot.nextStep.text}>{row.snapshot.nextStep.text}</small>
                      </>
                    ) : '-'}
                  </td>
                  <td>{completenessLabel(row.snapshot?.requirementCompleteness)}</td>
                  <td className="analysis-cell-follow">
                    <span className={row.followUp?.lastSpeaker === 'customer' ? 'waiting' : ''}>{lastSpeakerLabel(row.followUp?.lastSpeaker)}</span>
                    <small>{formatDateTime(row.followUp?.lastMessageAt ?? row.lastMessageAt)}</small>
                    {row.followUp?.missedCount ? <em className="analysis-flag missed">漏跟</em> : null}
                    {row.followUp && row.followUp.slowReplyCount > 0 ? <em className="analysis-flag slow">超时 ×{row.followUp.slowReplyCount}</em> : null}
                  </td>
                  <td><StatusMark row={row} /></td>
                  <td className="analysis-cell-headline" title={row.snapshot?.action}>{row.snapshot?.headline ?? '尚未分析'}</td>
                </tr>
              ))}
            </tbody>
          ))}
        </table>
        {rows.length === 0 ? <div className="analysis-empty">暂无符合条件的客户群</div> : null}
      </div>
    </div>
  );
}

function StatusMark({ row }: { row: GroupAnalysisOverviewRow }) {
  if (row.latestRun?.status === 'running') return <em className="analysis-status running">分析中</em>;
  if (row.snapshot && row.latestMessageId !== null && row.latestMessageId > row.snapshot.analyzedThroughMessageId) {
    return <em className="analysis-status stale">有新消息</em>;
  }
  if (row.latestRun?.status === 'failed' && (!row.snapshot || row.latestRun.id > row.snapshot.id)) {
    return <em className="analysis-status failed" title={runErrorLabel(row.latestRun.errorCode)}>最近失败</em>;
  }
  if (!row.snapshot) return <em className="analysis-status none">未分析</em>;
  return <em className="analysis-status ok">最新</em>;
}

function emptyPagination(): ListConversationsPagination {
  return { limit: PAGE_SIZE, offset: 0, count: 0, hasPrevious: false, hasNext: false, nextOffset: null, previousOffset: null };
}

/** 漏跟 / 流失明细 / 风险单三个页签：带原话的名单（与看板共用指标接口），风险单行可直接核实 / 误报（admin）。 */
function EvidenceListPane({ view, isAdmin, reloadKey, onToast, onSelect, onOpenEvidence, onReview }: {
  view: 'missed' | 'churn' | 'risk';
  isAdmin: boolean;
  reloadKey: number;
  onToast(message: string, type?: 'success' | 'error'): void;
  onSelect(id: number): void;
  onOpenEvidence(conversationId: number, messageId: number, rule: GroupDashboardEvidenceRule): void;
  onReview(conversationId: number, flagKey: string, status: GroupComplianceReviewStatus): void;
}) {
  if (view === 'missed') {
    return (
      <div className="analysis-ranking">
        <MetricList
          metric="missed"
          days={VIEW_DAYS}
          title="漏跟"
          hint="客户发了、没人回（最后一句是客户说的，之后 ≥ 2 小时；含历史）· 每条带客户那句话"
          columns={['客户群', '客户最后一句', '判定', '原话', '']}
          reloadKey={reloadKey}
          pageSize={20}
          renderRow={(item) => {
            const row = item as GroupDashboardMissedItem;
            return (
              <tr key={row.conversationId}>
                <td><strong className="analysis-link" onClick={() => onSelect(row.conversationId)}>{row.clusterName || `群 ${row.conversationId}`}</strong></td>
                <td>{formatDateTime(row.messageAt)}</td>
                <td>{row.verdict}</td>
                <td><button type="button" className="button ghost" onClick={() => onOpenEvidence(row.conversationId, row.messageId, 'missed')}>看原话</button></td>
                <td><button type="button" className="button ghost" onClick={() => onSelect(row.conversationId)}>看分析</button></td>
              </tr>
            );
          }}
          onToast={onToast}
        />
      </div>
    );
  }
  if (view === 'churn') {
    return (
      <div className="analysis-ranking">
        <MetricList
          metric="churn"
          days={VIEW_DAYS}
          title="流失明细"
          hint="最后一条客户消息距今 ≥ 180 天且窗口内我方没回 · 每群一条证据；预计损失 = 在途金额区间之和"
          columns={['客户群', '最后一句', '沉默', '为什么算流失', '证据', '']}
          reloadKey={reloadKey}
          pageSize={20}
          renderRow={(item) => {
            const row = item as GroupDashboardChurnItem;
            return (
              <tr key={row.conversationId}>
                <td><strong className="analysis-link" onClick={() => onSelect(row.conversationId)}>{row.clusterName || `群 ${row.conversationId}`}</strong></td>
                <td>{formatDateTime(row.lastMessageAt)}</td>
                <td>{row.silentDays} 天</td>
                <td>{row.verdict}</td>
                <td><button type="button" className="button ghost" onClick={() => onOpenEvidence(row.conversationId, row.lastCustomerMessageId, 'missed')}>证据</button></td>
                <td><button type="button" className="button ghost" onClick={() => onSelect(row.conversationId)}>看分析</button></td>
              </tr>
            );
          }}
          onToast={onToast}
        />
      </div>
    );
  }
  return (
    <div className="analysis-ranking">
      <MetricList
        metric="risk"
        days={VIEW_DAYS}
        title="风险单"
        hint="AI 从群聊识别，只认我方账号的发言，每条带原话；老板核实后才计入，误报一键剔除"
        columns={['客户群', '类型', '原话（AI 摘述）', '状态', '']}
        reloadKey={reloadKey}
        pageSize={20}
        renderRow={(item) => {
          const row = item as GroupDashboardRiskItem;
          return (
            <>
              {row.flags.map((flag) => (
                <tr key={flag.flagKey}>
                  <td><strong className="analysis-link" onClick={() => onSelect(row.conversationId)}>{row.clusterName || `群 ${row.conversationId}`}</strong></td>
                  <td><span className="analysis-severity severity-high">{complianceLabel(flag.type)}</span></td>
                  <td>{flag.text}</td>
                  <td><span className={`analysis-review-status status-${flag.status}`}>{flag.status === 'confirmed' ? '已确认' : flag.status === 'false_positive' ? '误报' : '待核实'}</span></td>
                  <td className="analysis-review-cell">
                    {flag.firstEvidenceMessageId ? <button type="button" className="button ghost" onClick={() => onOpenEvidence(row.conversationId, flag.firstEvidenceMessageId!, 'missed')}>看原话</button> : null}
                    {isAdmin && flag.status !== 'confirmed' ? <button type="button" className="button" onClick={() => onReview(row.conversationId, flag.flagKey, 'confirmed')}>核实</button> : null}
                    {isAdmin && flag.status !== 'false_positive' ? <button type="button" className="button ghost" onClick={() => onReview(row.conversationId, flag.flagKey, 'false_positive')}>误报</button> : null}
                    {isAdmin && flag.status !== 'pending' ? <button type="button" className="button ghost" onClick={() => onReview(row.conversationId, flag.flagKey, 'pending')}>撤回</button> : null}
                  </td>
                </tr>
              ))}
              {row.flags.length === 0 ? <tr key={`empty-${row.conversationId}`}><td colSpan={5}>{row.clusterName} · 旧快照无可解析的风险单</td></tr> : null}
            </>
          );
        }}
        onToast={onToast}
      />
    </div>
  );
}
